import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Staggered entrance — each block glides up out of a soft blur into place
      gsap.from(
        [
          '.hero__logo',
          '.hero__badge',
          '.hero__title',
          '.hero__subtitle',
          '.hero__actions',
          '.hero__highlights',
          '.hero__scroll',
        ],
        {
          y: 46,
          autoAlpha: 0,
          filter: 'blur(6px)',
          duration: 1.1,
          ease: 'power3.out',
          stagger: 0.12,
          delay: 0.15,
          clearProps: 'transform,filter',
        }
      );

      // Scroll-linked drift: the content eases up, scales in and fades as the
      // hero leaves the viewport, scrubbed to scroll position
      gsap.to(contentRef.current, {
        y: -120,
        scale: 0.96,
        autoAlpha: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });

      // Parallax the glow backdrop a touch slower for depth
      gsap.to('.hero__background', {
        yPercent: 18,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="hero" aria-label="Introduction" ref={sectionRef}>
      {/* Background with dark gradient overlay */}
      {/* Image path set here, not in global.css: CRA's css-loader would try to bundle a
          root-relative url() from src/ instead of serving it from public/ */}
      <div
        className="hero__background"
        aria-hidden="true"
        style={{ '--hero-image': "url('/assets/portfolio/hero-blue-hour-lake.jpg')" }}
      >
        <div className="hero__overlay" />
      </div>

      <div className="container hero__container">
        <div className="hero__content" ref={contentRef}>

          <a href="/" className="hero__logo" aria-label="Justin Shamoka Photography Home">
            <img
              src="/assets/logo/horizontal-banner-light.svg"
              alt="Justin Shamoka Photography logo"
              className="hero__logo-image"
            />
          </a>
         

          <h1 className="hero__title">
           Professional & Dedicated<br />
            <span className="hero__title--accent">Real-Estate Photographer.</span>
          </h1>
          <span className="hero__badge">
            <span className="hero__badge-dot" aria-hidden="true" />
            Fully Insured &amp; Bonded
          </span>
          <p className="hero__subtitle">
            Bright, detailed interior and exterior photography for agents and homeowners
            who want their listings to stand out — shot by a dedicated photographer who
            works around your schedule and delivers in 24–48 hours.
          </p>

          <div className="hero__actions">
            <a href="#contact" className="btn btn--primary">
              Book a Listing Shoot
            </a>
            <a href="#portfolio" className="btn btn--secondary">
              View Real Estate Portfolio
            </a>
          </div>

          <div className="hero__highlights">
            <div className="hero__stat">
              <span className="hero__stat-num">1-on-1</span>
              <span className="hero__stat-label">Dedicated Photographer</span>
            </div>
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <span className="hero__stat-num">24–48hr</span>
              <span className="hero__stat-label">Turnaround</span>
            </div>
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <span className="hero__stat-num">100%</span>
              <span className="hero__stat-label">Focused on You</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <a href="#services" className="hero__scroll" aria-label="Scroll to services">
        <span className="hero__scroll-text">Explore</span>
        <svg
          className="hero__scroll-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
        </svg>
      </a>
    </section>
  );
};

export default Hero;
